import { OfferFeature, OfferIdentificationStyle, OfferLocalizedText, OfferRegistrationTerms } from '../offer.data';

/** Default offer feature configuration shared by estate and company scopes. */
export const DEFAULT_OFFER_FEATURE: Readonly<OfferFeature> = Object.freeze({
  enabled: false,
  mobileRegistrationRequired: false,
  checkOffersUserAvailability: false,
  identificationStyle: OfferIdentificationStyle.FLIKO
});

/**
 * Checks a complete offer feature value received at runtime.
 * @param {unknown} feature Value to check
 * @return {boolean}
 */
export function isOfferFeature(feature: unknown): feature is OfferFeature {
  if (typeof feature !== 'object' || feature === null || Array.isArray(feature)) return false;
  const value = feature as Record<string, unknown>;
  const allowed = new Set([
    'enabled',
    'mobileRegistrationRequired',
    'checkOffersUserAvailability',
    'identificationStyle',
    'registrationTerms'
  ]);
  return (
    Object.keys(value).every((key) => allowed.has(key)) &&
    typeof value.enabled === 'boolean' &&
    typeof value.mobileRegistrationRequired === 'boolean' &&
    typeof value.checkOffersUserAvailability === 'boolean' &&
    Object.values(OfferIdentificationStyle).includes(value.identificationStyle as OfferIdentificationStyle) &&
    (value.registrationTerms == null || isOfferRegistrationTerms(value.registrationTerms))
  );
}

/**
 * Returns a complete offer feature configuration with defaults for missing or invalid values.
 * @param {Partial<OfferFeature> | null | undefined} feature
 * @return {OfferFeature}
 */
export function getOfferFeatureWithDefaults(feature: Partial<OfferFeature> | null | undefined): OfferFeature {
  const identificationStyle = Object.values(OfferIdentificationStyle).includes(
    feature?.identificationStyle as OfferIdentificationStyle
  )
    ? (feature?.identificationStyle as OfferIdentificationStyle)
    : DEFAULT_OFFER_FEATURE.identificationStyle;
  const registrationTerms = feature?.registrationTerms;

  return {
    enabled: feature?.enabled === true,
    mobileRegistrationRequired: feature?.mobileRegistrationRequired === true,
    checkOffersUserAvailability: feature?.checkOffersUserAvailability === true,
    identificationStyle,
    ...(registrationTerms === null || isOfferRegistrationTerms(registrationTerms) ? { registrationTerms } : {})
  };
}

/** Checks localized registration document links and their optional labels. */
function isOfferRegistrationTerms(value: unknown): value is OfferRegistrationTerms {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const terms = value as Record<string, unknown>;
  return (
    Object.keys(terms).every((key) => key === 'url' || key === 'urlTitle') &&
    isLocalizedText(terms.url) &&
    Object.values(terms.url).every(isHttpsUrl) &&
    (terms.urlTitle == null || isLocalizedText(terms.urlTitle))
  );
}

/** Checks nonempty language maps used by document links and labels. */
function isLocalizedText(value: unknown): value is OfferLocalizedText {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const entries = Object.entries(value);
  return (
    entries.length > 0 &&
    entries.every(([language, text]) => language.trim().length > 0 && typeof text === 'string' && text.trim().length > 0)
  );
}

/** Accepts complete HTTPS download URLs, including Firebase Storage download links. */
function isHttpsUrl(value: string): boolean {
  if (!value.startsWith('https://') || /\s/.test(value)) return false;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && url.hostname !== '' && url.username === '' && url.password === '';
  } catch {
    return false;
  }
}
